import { createSlice } from '@reduxjs/toolkit'
import { deleteEntry, copyEntry } from 'apps/builder/store/actions/entries'
import { loadFormData } from 'apps/builder/store/actions/form'
import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'
import {
  copyTableOrSimpleLoopSheet,
  copySheet,
  splitSheet,
  deleteSheet,
  deleteChildSheet,
  deleteLoopSheet,
  convertToLoopScreen,
  convertToTableScreen,
  promoteToLoopScreen,
  promoteToTableScreen,
  updateSheetSection,
} from 'apps/builder/store/actions/sheets'
import { updateIndex } from 'apps/builder/store/actions/entries'
import { importFormData } from 'apps/builder/store/actions/importData'
import {
  guid,
  isTrueStringMatch,
  sheetType,
  NORMAL_SHEET,
} from 'apps/builder/lib/utils'
import { deterministicGuid } from 'apps/builder/lib/hash'

const filterDeletedConditions = (state, action) => {
  const { id, additionalSheets = [] } = action.payload
  const sheetsToDelete = [id, ...additionalSheets.map(({ id }) => id)]
  return state.filter(
    ({ sheet_id, entrySheetId }) =>
      !sheetsToDelete.includes(sheet_id) &&
      !sheetsToDelete.includes(entrySheetId)
  )
}

const handleConvertToLoopOrTable = (state, action) => {
  const { id, newSheetId, activeSheetEntryGuids, keyField } = action.payload
  
  return state.reduce((acc, condition) => {
    const { parentEntryGuid, entry, entrySheetId, sheet_id } = condition

    if (isTrueStringMatch(parentEntryGuid, keyField.guid)) {
      // Any conditions on the key field are removed
      return acc
    }

    if (isTrueStringMatch(sheet_id, id)) {
      // Keep conditions that exist on the active sheet or on entries within the active sheet
      acc.push({
        ...condition,
        // Sheet conditions on the active sheet are moved to the new loop parent sheet
        sheet_id: parentEntryGuid ? sheet_id : newSheetId,
        // Update the entry sheet id to the new loop parent sheet for conditions that depend on the key field
        entrySheetId: isTrueStringMatch(entry, keyField.guid)
          ? newSheetId
          : entrySheetId,
      })
    } else if (!activeSheetEntryGuids.includes(`${entry}`)) {
      // Keep entry/sheet conditions outside of the active sheet that do not depend on the active sheet entries
      acc.push({ ...condition })
    }

   return acc
  }, [])
}

export const conditionSlice = createSlice({
  name: 'conditions',
  initialState: [],
  reducers: {
    addCondition(state, action) {
      state.push(action.payload)
    },
    updateCondition: (state, action) => {
      const { id } = action.payload
      return state.map(entry => {
        if (id !== entry.id) return entry
        return {
          ...entry,
          ...action.payload,
        }
      })
    },
    deleteCondition(state, action) {
      const id = action.payload
      return state.filter(entry => entry.id != id)
    },
  },
  extraReducers: {
    [loadFormData]: (state, action) => {
      const { conditions } = action.payload
      return conditions
    },
    [saveApp.fulfilled]: (state, action) => {
      const { entities } = action.payload
      const { conditions = [] } = entities
      return conditions
    },
    [deleteEntry]: (state, action) => {
      const { guid: deletedEntry } = action.payload
      return state.filter(
        ({ entry, parentEntryGuid }) =>
          entry != deletedEntry && parentEntryGuid != deletedEntry
      )
    },
    [deleteSheet]: filterDeletedConditions,
    [deleteChildSheet]: filterDeletedConditions,
    [deleteLoopSheet]: (state, action) => {
      const {
        id: oldKeyfieldSheetId,
        directDescendants,
        keyField,
      } = action.payload
      const firstChildSheetType = sheetType(directDescendants[0])
      const { guid: keyFieldGuid } = keyField
      const newKeyfieldSheetId =
        firstChildSheetType === NORMAL_SHEET ? directDescendants[0].id : null

      const newState = state.filter(condition => {
        const { sheet_id, entry, parentEntryGuid } = condition

        if (!parentEntryGuid) {
          // Sheet conditions don't have parentEntryGuids
          if (isTrueStringMatch(sheet_id, oldKeyfieldSheetId)) {
            // Delete any sheet conditions from the deleted sheet
            return false
          }

          if (
            newKeyfieldSheetId &&
            isTrueStringMatch(entry, keyFieldGuid) &&
            isTrueStringMatch(sheet_id, newKeyfieldSheetId)
          ) {
            // Delete any sheet conditions from the sheet that the keyfield was moved to if they depended on the keyfield
            return false
          }
        }

        if (!newKeyfieldSheetId && isTrueStringMatch(entry, keyFieldGuid)) {
          // Delete all screen and entry conditions that depended on the keyfield if the keyfield is deleted
          return false
        }

        return true
      })

      return newKeyfieldSheetId
        ? newState.map(condition => {
            if (isTrueStringMatch(condition.entry, keyFieldGuid)) {
              // Update any conditions that depend on the keyfield to the new sheet id of the keyfield
              return {
                ...condition,
                entrySheetId: newKeyfieldSheetId,
              }
            }
            return condition
          })
        : newState
    },
    [convertToLoopScreen]: handleConvertToLoopOrTable,
    [promoteToLoopScreen]: handleConvertToLoopOrTable,
    [convertToTableScreen]: handleConvertToLoopOrTable,
    [promoteToTableScreen]: handleConvertToLoopOrTable,
    // conditions don't care about positions, but they
    // do care about sheets
    [updateIndex]: (state, action) => {
      const { guid: movedGuid, oldSheet, sheet } = action.payload
      // no sheet changes, bail
      if (sheet === oldSheet) {
        return state
      }
      // if the sheet changed, update any entries or parentEntryGuids that changed sheets
      return state.map(condition => {
        const { parentEntryGuid, entry } = condition
        if (isTrueStringMatch(entry, movedGuid)) {
          return {
            ...condition,
            entrySheetId: sheet,
          }
        } else if (
          parentEntryGuid &&
          isTrueStringMatch(parentEntryGuid, movedGuid)
        ) {
          return {
            ...condition,
            sheet_id: sheet,
          }
        }
        return condition
      }, [])
    },
    [copyEntry]: (state, action) => {
      const { guid: originalGuid, seed } = action.payload
      const newConditions = state.reduce((acc, condition) => {
        const { parentEntryGuid } = condition
        if (isTrueStringMatch(parentEntryGuid, originalGuid)) {
          acc.push({
            ...condition,
            id: guid(),
            parentEntryGuid: deterministicGuid(parentEntryGuid, seed),
          })
        }
        return acc
      }, [])
      return [...state, ...newConditions]
    },
    [copySheet]: (state, action) => {
      const { id, newId, seed } = action.payload
      // find all matching screen conditions
      const newConditions = state.reduce((acc, condition) => {
        const { sheet_id, parentEntryGuid, entrySheetId, entry } = condition

        // only consider conditions relating to the current sheet
        if (isTrueStringMatch(sheet_id, id)) {
          const newCondition = {
            ...condition,
            id: guid(),
            sheet_id: newId,
          }
          // if this is a entry condition...
          if (parentEntryGuid) {
            // if the condition refered to an entry on the copied sheet
            // it needs to refer to the clone
            if (isTrueStringMatch(sheet_id, entrySheetId)) {
              newCondition.entry = deterministicGuid(entry, seed)
              newCondition.entrySheetId = newId
            }
            // or else just set the condition to depend on the original field
            // entry and entrySheetId stay the same
            newCondition.parentEntryGuid = deterministicGuid(
              parentEntryGuid,
              seed
            )
          }
          acc.push(newCondition)
        }
        return acc
      }, [])
      return [...state, ...newConditions]
    },
    [splitSheet]: (state, action) => {
      const { newId, sheetEntries, position } = action.payload

      const newSheetEntries = sheetEntries.reduce(
        (acc, entry) => {
          if (entry.position > position) {
              acc.push(`${entry.guid}`)
          }
          return acc
        }, [])

      return state.map(condition => {
        const { parentEntryGuid, entry } = condition

        const updatedCondition = { ...condition }
        if (newSheetEntries.includes(`${parentEntryGuid}`)) {
          // Update to the new sheet id for any entries that were split
          updatedCondition.sheet_id = newId
        }
        if (newSheetEntries.includes(`${entry}`)) {
          // Update to the new entry sheet id for any conditions that depend on the entries that were split
          updatedCondition.entrySheetId = newId
        }

        return updatedCondition
      })
    },
    [copyTableOrSimpleLoopSheet]: (state, action) => {
      const { sheetsToCopy, newMultiSheetGuid, seed, newSheetGuid } = action.payload
      // find all matching screen conditions, then handle entries
      const newConditions = state.reduce((acc, condition) => {
        const { sheet_id, parentEntryGuid, entrySheetId, entry } = condition
        
        if (sheetsToCopy.map(String).includes(String(sheet_id))) {
          const newCondition = {
            ...condition,// existing condition
            id: guid(),
            sheet_id: parentEntryGuid// if no parentEntryGuid, this is a sheet condition
              ? newMultiSheetGuid
              : newSheetGuid,
          }
          if (parentEntryGuid) {
            // if parentEntryGuid exists, then sheet conditions must be changed.
            // conditions that depend on entries on the same sheet, e.g. multiSheetGuid
            if (isTrueStringMatch(sheet_id, entrySheetId)) {
              newCondition.entry = deterministicGuid(entry, seed)
              newCondition.entrySheetId = newMultiSheetGuid
            /// condition depends on the value of the keyfield
            } else if (sheetsToCopy.includes(String(entrySheetId))) {
              newCondition.entry = deterministicGuid(entry, seed)
              newCondition.entrySheetId = newSheetGuid  
            }
            newCondition.parentEntryGuid = deterministicGuid(
              parentEntryGuid,
              seed
            )
          }
          acc.push(newCondition)
        }
        return acc
      }, [])
      return [...state, ...newConditions]
    },
    [updateSheetSection]: (state, action) => {
      const {
        id,
        childSheets,
        standardPrimarySheets,
        currentSectionId,
        section_id,
      } = action.payload

      if (isTrueStringMatch(currentSectionId, section_id)) {
        // If the sheet is moved to a different position in the same section, do nothing
        return state
      }

      const affectedSheetIds = [
        `${id}`,
        ...childSheets.map(sheet => `${sheet.id}`),
      ]
      const standardPrimarySheetIds = standardPrimarySheets.map(
        ({ id }) => `${id}`
      )

      return state.filter(condition => {
        const conditionSheetId = `${condition.sheet_id}`
        const conditionEntrySheetId = `${condition.entrySheetId}`
        if (
          affectedSheetIds.includes(conditionSheetId) &&
          !standardPrimarySheetIds.includes(conditionEntrySheetId) &&
          !affectedSheetIds.includes(conditionEntrySheetId)
        ) {
          // Remove any conditions on the moved sheet(s) if the dependent entry sheet id is:
          // Not on an entry in a standard primary sheet or in the sheet(s) that were moved
          return false
        }
        
        if (
          affectedSheetIds.includes(conditionEntrySheetId) &&
          !affectedSheetIds.includes(conditionSheetId)
        ) {
          // Remove any conditions on other sheets that depend on an entry in the moved sheet(s)
          return false
        }

        return true
      })
    },
    [importFormData]: (state, action) => {
      const { conditions = [] } = action.payload
      return [...state, ...conditions]
    },
  },
})

const { actions, reducer } = conditionSlice

export const conditionActions = actions
export const conditionReducer = reducer
